import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getOrder, updateOrderStatus } from '../../firebase/database';
import { formatPrice, formatDate, STATUS_COLORS, ORDER_STATUSES } from '../../utils/helpers';
import { Spinner } from '../../components/common/Loader';
import toast from 'react-hot-toast';

const AdminOrderView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => { getOrder(id).then(o => { if (!o) navigate('/admin/orders'); setOrder(o); }).finally(() => setLoading(false)); }, [id]);

  const handleStatus = async (status) => {
    setUpdating(true);
    try { await updateOrderStatus(id, status); setOrder(o => ({ ...o, status })); toast.success('Status updated!'); }
    catch { toast.error('Failed'); } finally { setUpdating(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg"/></div>;
  if (!order) return null;

  const statusFlow = ['pending','confirmed','processing','shipped','delivered'];

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/orders" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#1a3a6e] transition-colors font-medium">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
          All Orders
        </Link>
        <span className="text-gray-300">/</span>
        <span className="font-bold text-gray-900">#{order.orderId||order.id?.slice(0,8).toUpperCase()}</span>
        <span className={`text-xs font-bold px-3 py-1 rounded-full capitalize ml-auto ${STATUS_COLORS[order.status]||'bg-gray-100 text-gray-600'}`}>{order.status}</span>
      </div>

      {/* Status Progress */}
      {order.status !== 'cancelled' && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h3 className="font-bold text-gray-900 mb-4 text-sm">Order Progress</h3>
          <div className="flex items-center gap-0 overflow-x-auto">
            {statusFlow.map((s, i) => {
              const currentIdx = statusFlow.indexOf(order.status);
              const done = i <= currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s} className="flex items-center flex-shrink-0">
                  <div className="flex flex-col items-center gap-1.5">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs transition-all ${done ? 'bg-[#1a3a6e] text-white shadow-md' : 'bg-gray-100 text-gray-400'} ${active ? 'ring-4 ring-[#1a3a6e]/20' : ''}`}>
                      {done && !active ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                      ) : i + 1}
                    </div>
                    <span className={`text-[10px] font-semibold capitalize whitespace-nowrap ${done ? 'text-[#1a3a6e]' : 'text-gray-400'}`}>{s}</span>
                  </div>
                  {i < statusFlow.length - 1 && (
                    <div className={`w-12 sm:w-24 h-0.5 mx-1 mb-5 transition-colors ${i < statusFlow.indexOf(order.status) ? 'bg-[#1a3a6e]' : 'bg-gray-200'}`}/>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Items + Update */}
        <div className="lg:col-span-2 space-y-5">
          {/* Items */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100"><h3 className="font-bold text-gray-900">Order Items ({order.items?.length})</h3></div>
            <div className="divide-y divide-gray-50">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-4">
                  <img src={item.image||'https://via.placeholder.com/60'} alt={item.title}
                    className="w-14 h-14 object-cover flex-shrink-0 rounded-lg"
                    onError={e=>e.target.src='https://via.placeholder.com/60?text=Item'}/>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                    {item.selectedVariation&&<p className="text-gray-400 text-xs mt-0.5">{item.selectedVariation}</p>}
                    <p className="text-gray-400 text-xs">Qty: {item.quantity} × {formatPrice(item.price)}</p>
                  </div>
                  <p className="font-bold text-gray-900 flex-shrink-0">{formatPrice(item.price*item.quantity)}</p>
                </div>
              ))}
            </div>
            <div className="p-4 bg-gray-50 space-y-2 text-sm">
              <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{formatPrice(order.subtotal||0)}</span></div>
              <div className="flex justify-between text-gray-500"><span>Shipping</span><span>{order.shippingCost===0?'FREE':formatPrice(order.shippingCost||0)}</span></div>
              <div className="flex justify-between font-black text-base border-t border-gray-200 pt-2"><span>Total</span><span className="text-[#1a3a6e]">{formatPrice(order.total||0)}</span></div>
            </div>
          </div>

          {/* Update Status */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h3 className="font-bold text-gray-900 mb-3">Update Order Status</h3>
            <div className="flex flex-wrap gap-2">
              {ORDER_STATUSES.map(s => (
                <button key={s} onClick={() => handleStatus(s)} disabled={order.status===s||updating}
                  className={`px-4 py-2 text-xs font-bold capitalize transition-all ${order.status===s?'bg-[#1a3a6e] text-white cursor-default':'bg-gray-100 text-gray-600 hover:bg-[#1a3a6e]/10 hover:text-[#1a3a6e]'} ${s==='cancelled'&&order.status!==s?'hover:bg-red-50 hover:text-red-600':''}`}>
                  {s}
                </button>
              ))}
              {updating && <Spinner size="sm"/>}
            </div>
          </div>
        </div>

        {/* Right: Customer + Shipping */}
        <div className="space-y-5">
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">Customer</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1a3a6e] rounded-full flex items-center justify-center text-white font-black flex-shrink-0">
                  {order.customerName?.[0]?.toUpperCase()||'G'}
                </div>
                <div>
                  <p className="font-bold text-gray-900">{order.customerName||'Guest'}</p>
                  <p className="text-gray-400 text-xs">{order.userId==='guest'?'Guest Order':'Registered User'}</p>
                </div>
              </div>
              {[
                { icon:'mail', val: order.email },
                { icon:'phone', val: order.phone },
              ].map(({ icon, val }) => val && (
                <div key={icon} className="flex items-center gap-2 text-gray-600">
                  {icon==='mail'
                    ? <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                    : <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>}
                  <span>{val}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wider">Shipping Address</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p className="font-semibold text-gray-900">{order.shippingAddress?.address}</p>
              <p>{order.shippingAddress?.city}{order.shippingAddress?.province?`, ${order.shippingAddress.province}`:''}, Pakistan</p>
              <p className="text-gray-400 text-xs mt-2 capitalize">Method: {order.shippingMethod}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-5">
            <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wider">Payment</h3>
            <p className="text-sm font-semibold text-gray-900 capitalize">{order.paymentMethod==='cod'?'Cash on Delivery':order.paymentMethod}</p>
            <p className="text-xs text-gray-400 mt-1">Placed on {formatDate(order.createdAt)}</p>
          </div>

          {order.notes && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <p className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-1">Order Notes</p>
              <p className="text-sm text-amber-700">{order.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default AdminOrderView;
